import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const GOLD = '#D4AF37';
const GOLD2 = '#f5c842';
const BG = '#050810';
const BG_CARD = '#0d1117';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';

const inputStyle = {
  width: '100%',
  background: '#0a0f1e',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '14px 16px',
  color: '#fff',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block',
  color: TEXT_SEC,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 8,
};

const PRESET_AMOUNTS = [
  { value: 25, name: 'The Refresh', desc: 'Perfect for a quick touch-up' },
  { value: 50, name: 'The Classic', desc: 'A full service experience' },
  { value: 100, name: 'The Premium', desc: 'The full luxury experience' },
];

function ScissorsIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3"/>
      <circle cx="6" cy="18" r="3"/>
      <line x1="20" y1="4" x2="8.12" y2="15.88"/>
      <line x1="14.47" y1="14.48" x2="20" y2="20"/>
      <line x1="8.12" y1="8.12" x2="12" y2="12"/>
    </svg>
  );
}

function GiftCardMockup({ amount }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #0a0f1e 0%, #111827 50%, #0a0f1e 100%)',
      border: `1px solid rgba(212,175,55,0.4)`,
      borderRadius: 18,
      padding: '28px 32px',
      width: '100%',
      maxWidth: 380,
      margin: '0 auto',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `0 0 60px rgba(212,175,55,0.12), 0 20px 60px rgba(0,0,0,0.6)`,
      aspectRatio: '1.6',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
      {/* Sheen overlay */}
      <div style={{
        position: 'absolute', top: '-50%', left: '-30%', width: '60%', height: '200%',
        background: 'linear-gradient(105deg, transparent 40%, rgba(212,175,55,0.06) 50%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{
            fontSize: 22, fontWeight: 900, letterSpacing: '0.15em',
            background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>MANE</div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 2 }}>
            Gift Card
          </div>
        </div>
        <ScissorsIcon size={24} />
      </div>

      {/* Amount */}
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: amount && String(amount).length > 3 ? 42 : 52,
          fontWeight: 900,
          background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          lineHeight: 1,
        }}>
          {amount ? `$${amount}` : '?'}
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Christian G. Flair Studio
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(212,175,55,0.08)',
          border: '1px solid rgba(212,175,55,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ color: GOLD, fontSize: 16 }}></div>
        </div>
      </div>
    </div>
  );
}

// CSS-only confetti
function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => i);
  const colors = [GOLD, GOLD2, '#fff', '#f59e0b', '#10b981'];
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 9999 }}>
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      {pieces.map(i => (
        <div key={i} style={{
          position: 'absolute',
          left: `${Math.random() * 100}%`,
          top: `-10px`,
          width: Math.random() * 8 + 4,
          height: Math.random() * 8 + 4,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          background: colors[Math.floor(Math.random() * colors.length)],
          animation: `confettiFall ${Math.random() * 2 + 1.5}s ease-in ${Math.random() * 0.8}s forwards`,
        }} />
      ))}
    </div>
  );
}

export default function GiftCardStore() {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [form, setForm] = useState({
    recipientName: '',
    recipientEmail: '',
    fromName: '',
    fromEmail: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { code, amount }
  const [showConfetti, setShowConfetti] = useState(false);

  const displayAmount = isCustom ? (parseFloat(customAmount) || 0) : selectedAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = displayAmount;
    if (!amt || amt < 10 || amt > 500) {
      setError('Please choose an amount between $10 and $500.');
      return;
    }
    if (!form.recipientName) { setError('Recipient name is required.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/gift-cards/purchase', {
        amount: amt,
        recipient_name: form.recipientName,
        recipient_email: form.recipientEmail,
        from_name: form.fromName,
        from_email: form.fromEmail,
        message: form.message,
      });
      setSuccess({ code: data.code, amount: amt });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3500);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        {showConfetti && <Confetti />}
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
          {/* Success icon */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(16,185,129,0.1)', border: '2px solid rgba(16,185,129,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', fontSize: 32,
          }}></div>

          <h1 style={{
            fontSize: 32, fontWeight: 900, marginBottom: 12,
            background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>Gift Card Sent!</h1>
          <p style={{ color: TEXT_SEC, fontSize: 16, marginBottom: 40 }}>
            {form.recipientEmail ? `We emailed ${form.recipientEmail} their gift card.` : 'Your gift card is ready to share.'}
          </p>

          {/* Card mockup */}
          <GiftCardMockup amount={success.amount} />

          {/* Code */}
          <div style={{
            marginTop: 32, background: BG_CARD, border: `1px solid rgba(212,175,55,0.3)`,
            borderRadius: 16, padding: '24px',
          }}>
            <div style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Gift Card Code</div>
            <div style={{
              fontFamily: 'monospace', fontSize: 26, fontWeight: 900, letterSpacing: '0.18em',
              color: GOLD, marginBottom: 16,
            }}>{success.code}</div>
            <button
              onClick={() => navigator.clipboard.writeText(success.code)}
              style={{
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, color: '#000',
                border: 'none', borderRadius: 10, padding: '12px 28px',
                fontWeight: 800, fontSize: 14, cursor: 'pointer',
              }}
            >
              Copy Code
            </button>
          </div>

          <div style={{ marginTop: 24, background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ color: TEXT_SEC, fontSize: 13 }}>
              Redeemable at <strong style={{ color: '#fff' }}>Christian G. Flair Studio</strong><br />
              Use when booking at <a href="https://joinmane.com/barber" style={{ color: GOLD }}>joinmane.com/barber</a>
            </div>
          </div>

          <div style={{ marginTop: 32, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => { setSuccess(null); setForm({ recipientName: '', recipientEmail: '', fromName: '', fromEmail: '', message: '' }); }}
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, color: '#000', border: 'none', borderRadius: 10, padding: '14px 28px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}
            >
              Buy Another
            </button>
            <Link to="/barber" style={{ background: 'transparent', color: TEXT_SEC, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 28px', fontWeight: 700, fontSize: 14, textDecoration: 'none', display: 'inline-block' }}>
              Book a Cut
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: BG, color: '#fff', fontFamily: 'inherit' }}>
      <style>{`
        .gc-amount-card { transition: all 0.2s ease; cursor: pointer; }
        .gc-amount-card:hover { border-color: rgba(212,175,55,0.5) !important; background: rgba(212,175,55,0.05) !important; transform: translateY(-2px); }
        .gc-amount-card.selected { border-color: ${GOLD} !important; background: rgba(212,175,55,0.08) !important; box-shadow: 0 0 0 1px ${GOLD}40; }
        .gc-input:focus { border-color: rgba(212,175,55,0.5) !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
      `}</style>

      {/* Nav bar */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 100, background: 'rgba(5,8,16,0.9)' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <span style={{ fontSize: 20, fontWeight: 900, background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.1em' }}>MANE</span>
        </Link>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Link to="/barber" style={{ color: TEXT_SEC, fontSize: 14, textDecoration: 'none', fontWeight: 600 }}>Book a Cut</Link>
        </div>
      </div>

      {/* Hero */}
      <div className="fade-up" style={{ textAlign: 'center', padding: '80px 24px 60px', maxWidth: 700, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 20, padding: '6px 16px', marginBottom: 28 }}>
          
          <span style={{ color: GOLD, fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Gift Cards</span>
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 900, lineHeight: 1.05, margin: '0 0 20px', letterSpacing: '-0.02em' }}>
          Give the Gift of a{' '}
          <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Fresh Cut
          </span>
        </h1>
        <p style={{ color: TEXT_SEC, fontSize: 18, lineHeight: 1.7, maxWidth: 480, margin: '0 auto' }}>
          The perfect gift for any occasion. Redeemable at Christian G. Flair Studio.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 80px' }}>
        {/* Two-column layout on wide screens */}
        <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Left: Amount selection + mockup */}
          <div style={{ flex: '1 1 340px', minWidth: 280 }}>
            <div className="fade-up" style={{ marginBottom: 40 }}>
              <GiftCardMockup amount={displayAmount || undefined} />
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 20 }}>Choose an Amount</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
              {PRESET_AMOUNTS.map(opt => (
                <div
                  key={opt.value}
                  className={`gc-amount-card${!isCustom && selectedAmount === opt.value ? ' selected' : ''}`}
                  onClick={() => { setSelectedAmount(opt.value); setIsCustom(false); }}
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 16, padding: '20px 14px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 26, fontWeight: 900, color: GOLD, marginBottom: 6 }}>${opt.value}</div>
                  <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{opt.name}</div>
                  <div style={{ color: TEXT_SEC, fontSize: 11 }}>{opt.desc}</div>
                </div>
              ))}
            </div>

            {/* Custom amount */}
            <div
              className={`gc-amount-card${isCustom ? ' selected' : ''}`}
              style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '16px 20px' }}
              onClick={() => setIsCustom(true)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Custom Amount</div>
                  <div style={{ color: TEXT_SEC, fontSize: 12 }}>$10 – $500</div>
                </div>
                <input
                  type="number"
                  min="10"
                  max="500"
                  value={customAmount}
                  onChange={e => { setCustomAmount(e.target.value); setIsCustom(true); }}
                  onClick={e => e.stopPropagation()}
                  placeholder="$75"
                  className="gc-input"
                  style={{ ...inputStyle, marginLeft: 'auto', width: 100, padding: '10px 14px', fontSize: 16, fontWeight: 700, textAlign: 'center' }}
                />
              </div>
            </div>
          </div>

          {/* Right: Personalize form */}
          <div style={{ flex: '1 1 340px', minWidth: 280 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 24 }}>Personalize It</h2>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* For section */}
              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24 }}>
                <div style={{ color: GOLD, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>For</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Recipient Name *</label>
                    <input
                      required
                      className="gc-input"
                      value={form.recipientName}
                      onChange={e => setForm(f => ({ ...f, recipientName: e.target.value }))}
                      placeholder="John Smith"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Recipient Email <span style={{ color: '#374151' }}>(optional — card will be emailed)</span></label>
                    <input
                      type="email"
                      className="gc-input"
                      value={form.recipientEmail}
                      onChange={e => setForm(f => ({ ...f, recipientEmail: e.target.value }))}
                      placeholder="john@example.com"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* From section */}
              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24 }}>
                <div style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>From</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Your Name</label>
                    <input
                      className="gc-input"
                      value={form.fromName}
                      onChange={e => setForm(f => ({ ...f, fromName: e.target.value }))}
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Your Email <span style={{ color: '#374151' }}>(for receipt)</span></label>
                    <input
                      type="email"
                      className="gc-input"
                      value={form.fromEmail}
                      onChange={e => setForm(f => ({ ...f, fromEmail: e.target.value }))}
                      placeholder="you@example.com"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label style={labelStyle}>Personal Message <span style={{ color: '#374151' }}>(optional)</span></label>
                <textarea
                  className="gc-input"
                  rows={3}
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Happy Birthday! Enjoy your fresh cut"
                  style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }}
                />
              </div>

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', color: '#ef4444', fontSize: 14 }}>
                  {error}
                </div>
              )}

              {/* Summary */}
              {displayAmount > 0 && (
                <div style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: TEXT_SEC, fontSize: 14 }}>Total</span>
                  <span style={{ color: GOLD, fontWeight: 900, fontSize: 22 }}>${displayAmount}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !displayAmount || displayAmount < 10}
                style={{
                  width: '100%',
                  background: loading || !displayAmount ? '#1a1a1a' : `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
                  color: loading || !displayAmount ? '#374151' : '#000',
                  border: 'none', borderRadius: 12,
                  padding: '18px', fontWeight: 800, fontSize: 16,
                  cursor: loading || !displayAmount ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s', letterSpacing: '0.02em',
                }}
              >
                {loading ? 'Processing…' : `Purchase Gift Card — $${displayAmount || '?'}`}
              </button>

              <p style={{ color: TEXT_SEC, fontSize: 12, textAlign: 'center', margin: 0 }}>
                Secure purchase · Delivered instantly to recipient's email
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: '24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#374151', fontSize: 20, fontWeight: 900, letterSpacing: '0.15em', background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MANE</span>
          <Link to="/barber" style={{ color: TEXT_SEC, fontSize: 13, textDecoration: 'none' }}>Book a Cut</Link>
          <Link to="/privacy" style={{ color: TEXT_SEC, fontSize: 13, textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ color: TEXT_SEC, fontSize: 13, textDecoration: 'none' }}>Terms</Link>
        </div>
        <div style={{ color: '#1f2937', fontSize: 12, marginTop: 12 }}>© 2026 Christian G. Flair Studio. Gift cards are non-refundable.</div>
      </div>
    </div>
  );
}
